import type {
  AcademicYearSummary,
  ApplicationSummary,
  ClassSummary,
  EnquirySummary,
} from '@school-erp/types';
import { createApplicationSchema, createEnquirySchema } from '@school-erp/validation';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiList } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  Pager,
  SchoolSelect,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';

const ENQUIRY_ACTIONS: Record<EnquirySummary['status'], string[]> = {
  new: ['contacted', 'closed'],
  contacted: ['closed'],
  application_issued: ['closed'],
  closed: ['new'],
};
const APPLICATION_ACTIONS: Record<ApplicationSummary['status'], string[]> = {
  submitted: ['under_review', 'rejected'],
  under_review: ['approved', 'rejected'],
  approved: ['rejected'],
  rejected: ['under_review'],
  admitted: [],
};

export function AdmissionsPage() {
  const [schoolId, setSchoolId] = useState('');
  const [tab, setTab] = useState<'enquiries' | 'applications'>('enquiries');

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <h1 className="text-2xl font-bold">{t('adm.title')}</h1>
      <SchoolSelect value={schoolId} onChange={setSchoolId} />
      {schoolId && (
        <>
          <div
            role="tablist"
            aria-label={t('adm.title')}
            className="flex gap-2 border-b border-slate-200"
          >
            {(['enquiries', 'applications'] as const).map((name) => (
              <button
                key={name}
                role="tab"
                type="button"
                aria-selected={tab === name}
                onClick={() => setTab(name)}
                className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${tab === name ? 'border-brand-700 text-brand-700' : 'border-transparent text-slate-600'}`}
              >
                {t(`adm.${name}` as MessageKey)}
              </button>
            ))}
          </div>
          {tab === 'enquiries' ? (
            <Enquiries schoolId={schoolId} />
          ) : (
            <Applications schoolId={schoolId} />
          )}
        </>
      )}
    </div>
  );
}

function Enquiries({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    studentName: '',
    parentName: '',
    contactPhone: '',
    interestedClass: '',
    source: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const key = ['admissions', schoolId, 'enquiries', page];

  const list = useQuery({
    queryKey: key,
    queryFn: () =>
      apiList<EnquirySummary>(`/schools/${schoolId}/admissions/enquiries`, {
        query: { page, pageSize: 10 },
      }),
    placeholderData: keepPreviousData,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admissions', schoolId] });
  const create = useMutation({
    mutationFn: (body: object) =>
      api(`/schools/${schoolId}/admissions/enquiries`, { method: 'POST', body }),
    onSuccess: () => {
      setAdding(false);
      setForm({
        studentName: '',
        parentName: '',
        contactPhone: '',
        interestedClass: '',
        source: '',
      });
      setMessage(null);
      void refresh();
    },
    onError: (e) => setMessage(errorMessage(e)),
  });
  const move = useMutation({
    mutationFn: ({ id, to }: { id: string; to: string }) =>
      api(`/schools/${schoolId}/admissions/enquiries/${id}/transition`, {
        method: 'POST',
        body: { to },
      }),
    onSuccess: refresh,
    onError: (e) => setMessage(errorMessage(e)),
  });

  return (
    <section aria-label={t('adm.enquiries')} className="space-y-3">
      {can('admissions.create', schoolId) && (
        <button
          type="button"
          aria-expanded={adding}
          className={secondaryButton}
          onClick={() => setAdding((o) => !o)}
        >
          {adding ? t('common.cancel') : t('adm.newEnquiry')}
        </button>
      )}
      {message && <Notice kind="error">{message}</Notice>}
      {adding && (
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            const candidate = Object.fromEntries(
              Object.entries(form).filter(([, v]) => v.trim() !== ''),
            );
            const parsed = createEnquirySchema.safeParse(candidate);
            if (!parsed.success) {
              setErrors(
                Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])),
              );
              return;
            }
            setErrors({});
            create.mutate(parsed.data);
          }}
          className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-3"
        >
          <TextField
            label={t('adm.studentName')}
            value={form.studentName}
            error={errors.studentName}
            onChange={(e) => setForm({ ...form, studentName: e.target.value })}
          />
          <TextField
            label={t('adm.parentName')}
            value={form.parentName}
            error={errors.parentName}
            onChange={(e) => setForm({ ...form, parentName: e.target.value })}
          />
          <TextField
            label={t('adm.phone')}
            value={form.contactPhone}
            error={errors.contactPhone}
            onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
          />
          <TextField
            label={t('adm.interestedClass')}
            value={form.interestedClass}
            onChange={(e) => setForm({ ...form, interestedClass: e.target.value })}
          />
          <TextField
            label={t('adm.source')}
            value={form.source}
            onChange={(e) => setForm({ ...form, source: e.target.value })}
          />
          <div className="self-end">
            <button type="submit" disabled={create.isPending} className={primaryButton}>
              {t('adm.save')}
            </button>
          </div>
        </form>
      )}
      {list.isPending && <p role="status">{t('common.loading')}</p>}
      {list.data && (
        <>
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                <tr>
                  <th scope="col" className="px-4 py-3">
                    {t('adm.number')}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t('adm.studentName')}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t('adm.parentName')}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t('adm.status')}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t('users.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.data.data.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-slate-600">
                      {t('adm.empty')}
                    </td>
                  </tr>
                )}
                {list.data.data.map((enq) => (
                  <tr key={enq.id}>
                    <td className="px-4 py-3 font-mono text-xs">{enq.enquiryNumber}</td>
                    <td className="px-4 py-3 font-medium">{enq.studentName}</td>
                    <td className="px-4 py-3">{enq.parentName}</td>
                    <td className="px-4 py-3">{t(`adm.status.${enq.status}` as MessageKey)}</td>
                    <td className="space-x-2 px-4 py-3">
                      {can('admissions.update', schoolId) &&
                        ENQUIRY_ACTIONS[enq.status].map((to) => (
                          <button
                            key={to}
                            type="button"
                            className={secondaryButton}
                            onClick={() => move.mutate({ id: enq.id, to })}
                          >
                            {t(`adm.action.${to}` as MessageKey)}
                          </button>
                        ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={page} totalPages={list.data.meta.totalPages} onPage={setPage} />
        </>
      )}
    </section>
  );
}

function Applications({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const list = useQuery({
    queryKey: ['admissions', schoolId, 'applications', page],
    queryFn: () =>
      apiList<ApplicationSummary>(`/schools/${schoolId}/admissions/applications`, {
        query: { page, pageSize: 10 },
      }),
    placeholderData: keepPreviousData,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admissions', schoolId] });
  const fail = (e: unknown) => setMessage({ kind: 'error', text: errorMessage(e) });

  const move = useMutation({
    mutationFn: ({ id, to, note }: { id: string; to: string; note?: string }) =>
      api(`/schools/${schoolId}/admissions/applications/${id}/transition`, {
        method: 'POST',
        body: { to, note },
      }),
    onSuccess: () => {
      setMessage(null);
      void refresh();
    },
    onError: fail,
  });
  const admit = useMutation({
    mutationFn: (id: string) =>
      api<{ admissionNumber: string }>(`/schools/${schoolId}/admissions/applications/${id}/admit`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: (student) => {
      setMessage({ kind: 'ok', text: t('adm.admitted', { number: student.admissionNumber }) });
      void refresh();
    },
    onError: fail,
  });

  function act(app: ApplicationSummary, to: string) {
    if (to === 'rejected') {
      const note = window.prompt(t('adm.notePrompt'));
      if (!note) return;
      move.mutate({ id: app.id, to, note });
    } else {
      move.mutate({ id: app.id, to });
    }
  }

  return (
    <section aria-label={t('adm.applications')} className="space-y-3">
      {can('admissions.create', schoolId) && (
        <button
          type="button"
          aria-expanded={adding}
          className={secondaryButton}
          onClick={() => setAdding((o) => !o)}
        >
          {adding ? t('common.cancel') : t('adm.newApplication')}
        </button>
      )}
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      {adding && (
        <NewApplication
          schoolId={schoolId}
          onCreated={() => {
            setAdding(false);
            void refresh();
          }}
        />
      )}
      {list.isPending && <p role="status">{t('common.loading')}</p>}
      {list.data && (
        <>
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                <tr>
                  <th scope="col" className="px-4 py-3">
                    {t('adm.number')}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t('users.name')}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t('students.class')}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t('adm.status')}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t('users.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.data.data.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-slate-600">
                      {t('adm.empty')}
                    </td>
                  </tr>
                )}
                {list.data.data.map((app) => (
                  <tr key={app.id}>
                    <td className="px-4 py-3 font-mono text-xs">{app.applicationNumber}</td>
                    <td className="px-4 py-3 font-medium">
                      {String(app.applicant.firstName)} {String(app.applicant.lastName)}
                    </td>
                    <td className="px-4 py-3">{app.className}</td>
                    <td className="px-4 py-3">{t(`adm.status.${app.status}` as MessageKey)}</td>
                    <td className="space-x-2 px-4 py-3">
                      {APPLICATION_ACTIONS[app.status]
                        .filter((to) =>
                          can(
                            to === 'approved' || to === 'rejected'
                              ? 'admissions.approve'
                              : 'admissions.update',
                            schoolId,
                          ),
                        )
                        .map((to) => (
                          <button
                            key={to}
                            type="button"
                            className={secondaryButton}
                            onClick={() => act(app, to)}
                          >
                            {t(`adm.action.${to}` as MessageKey)}
                          </button>
                        ))}
                      {app.status === 'approved' &&
                        can('admissions.approve', schoolId) &&
                        can('students.create', schoolId) && (
                          <button
                            type="button"
                            className={primaryButton}
                            disabled={admit.isPending}
                            onClick={() => admit.mutate(app.id)}
                          >
                            {t('adm.admit')}
                          </button>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={page} totalPages={list.data.meta.totalPages} onPage={setPage} />
        </>
      )}
    </section>
  );
}

function NewApplication({ schoolId, onCreated }: { schoolId: string; onCreated: () => void }) {
  const years = useQuery({
    queryKey: ['schools', schoolId, 'years'],
    queryFn: () => api<AcademicYearSummary[]>(`/schools/${schoolId}/academic-years`),
  });
  const classes = useQuery({
    queryKey: ['classes', schoolId],
    queryFn: () => api<ClassSummary[]>(`/schools/${schoolId}/classes`),
  });
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: 'female',
    guardianFirst: '',
    guardianLast: '',
    guardianEmail: '',
    classId: '',
    academicYearId: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);

  const yearId = form.academicYearId || years.data?.find((y) => y.isCurrent)?.id || '';
  const create = useMutation({
    mutationFn: (body: object) =>
      api(`/schools/${schoolId}/admissions/applications`, { method: 'POST', body }),
    onSuccess: onCreated,
    onError: (e) => setMessage(errorMessage(e)),
  });

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        const parsed = createApplicationSchema.safeParse({
          academicYearId: yearId,
          classId: form.classId,
          applicant: {
            firstName: form.firstName,
            lastName: form.lastName,
            dateOfBirth: form.dateOfBirth,
            gender: form.gender,
          },
          guardians: [
            {
              firstName: form.guardianFirst,
              lastName: form.guardianLast,
              email: form.guardianEmail,
              relationship: 'mother',
              isPrimary: true,
              isEmergencyContact: true,
            },
          ],
        });
        if (!parsed.success) {
          setErrors(
            Object.fromEntries(
              parsed.error.issues.map((i) => [i.path.slice(-1).join('.'), i.message]),
            ),
          );
          return;
        }
        setErrors({});
        create.mutate(parsed.data);
      }}
      className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-3"
    >
      <TextField
        label={t('students.firstName')}
        value={form.firstName}
        error={errors.firstName}
        onChange={(e) => setForm({ ...form, firstName: e.target.value })}
      />
      <TextField
        label={t('students.lastName')}
        value={form.lastName}
        error={errors.lastName}
        onChange={(e) => setForm({ ...form, lastName: e.target.value })}
      />
      <TextField
        label={t('students.dob')}
        type="date"
        value={form.dateOfBirth}
        error={errors.dateOfBirth}
        onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })}
      />
      <div>
        <label htmlFor="app-class" className="block text-sm font-medium">
          {t('adm.applyClass')}
        </label>
        <select
          id="app-class"
          value={form.classId}
          onChange={(e) => setForm({ ...form, classId: e.target.value })}
          className={selectClass}
        >
          <option value="" />
          {classes.data?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        {errors.classId && (
          <p role="alert" className="mt-1 text-xs text-red-700">
            {errors.classId}
          </p>
        )}
      </div>
      <TextField
        label={t('adm.guardianFirst')}
        value={form.guardianFirst}
        error={errors.firstName && !form.firstName ? undefined : undefined}
        onChange={(e) => setForm({ ...form, guardianFirst: e.target.value })}
      />
      <TextField
        label={t('adm.guardianLast')}
        value={form.guardianLast}
        onChange={(e) => setForm({ ...form, guardianLast: e.target.value })}
      />
      <TextField
        label={t('adm.guardianEmail')}
        type="email"
        value={form.guardianEmail}
        onChange={(e) => setForm({ ...form, guardianEmail: e.target.value })}
      />
      <div className="space-y-2 sm:col-span-3">
        {message && <Notice kind="error">{message}</Notice>}
        {Object.keys(errors).length > 0 && <Notice kind="error">{Object.values(errors)[0]}</Notice>}
        <button type="submit" disabled={create.isPending} className={primaryButton}>
          {t('adm.save')}
        </button>
      </div>
    </form>
  );
}
